/**
 * WASM converter service wrapper.
 * Provides a clean API for converting CUBE files to XMP.
 */

import {
  convertCubeToXmp,
  validateCubeFile,
  isConvertSuccess,
  isValidationValid,
  DEFAULT_CONVERT_OPTIONS,
  type WasmConvertOptions,
  type WasmConvertResult,
  type WasmValidationResult,
} from '@wasm/index';

import type { ConversionOptions } from '@stores/conversionStore';

/** Convert frontend options to WASM options format */
function toWasmOptions(
  cubeContent: string,
  filename: string,
  options: Partial<ConversionOptions>
): WasmConvertOptions {
  const wasmOptions: WasmConvertOptions = {
    cube_content: cubeContent,
    filename,
    requires_rgb_tables: options.requiresRgbTables ?? DEFAULT_CONVERT_OPTIONS.requires_rgb_tables,
    show_in_presets: options.showInPresets ?? DEFAULT_CONVERT_OPTIONS.show_in_presets,
    show_in_quick_actions: options.showInQuickActions ?? DEFAULT_CONVERT_OPTIONS.show_in_quick_actions,
    allow_large: options.allowLarge ?? DEFAULT_CONVERT_OPTIONS.allow_large,
  };

  if (options.name) {
    wasmOptions.name = options.name;
  }
  if (options.shortName) {
    wasmOptions.short_name = options.shortName;
  }
  if (options.sortName) {
    wasmOptions.sort_name = options.sortName;
  }
  if (options.group) {
    wasmOptions.group = options.group;
  }
  if (options.description) {
    wasmOptions.description = options.description;
  }
  if (options.lutSize !== undefined) {
    wasmOptions.lut_size = options.lutSize;
  }

  return wasmOptions;
}

/** Read file content as text */
export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/** Generate output filename from input filename */
export function generateOutputFilename(inputFilename: string): string {
  const lastDot = inputFilename.lastIndexOf('.');
  const baseName = lastDot > 0 ? inputFilename.slice(0, lastDot) : inputFilename;
  return `${baseName}.xmp`;
}

/** Validate a CUBE file */
export async function validateFile(file: File): Promise<WasmValidationResult> {
  const content = await readFileAsText(file);
  return validateCubeFile(content);
}

/** Convert a single CUBE file to XMP */
export async function convertFile(
  file: File,
  options: Partial<ConversionOptions> = {}
): Promise<WasmConvertResult> {
  const content = await readFileAsText(file);
  const wasmOptions = toWasmOptions(content, file.name, options);
  return convertCubeToXmp(wasmOptions);
}

/** Convert CUBE content string to XMP */
export function convertContent(
  content: string,
  filename: string,
  options: Partial<ConversionOptions> = {}
): WasmConvertResult {
  const wasmOptions = toWasmOptions(content, filename, options);
  return convertCubeToXmp(wasmOptions);
}

// Re-export utilities
export { isConvertSuccess, isValidationValid };
export type { WasmConvertResult, WasmValidationResult };
